import apiClient from './api';
import { apiService } from './api';
import { CompanySettings, UpdateCompanySettingsRequest, PrintOutputType } from '../types';
import { ApiResponse } from '../types';
import type { SalesInvoiceListDTO } from './salesInvoiceService';

export interface SalesSummaryDTO {
  totalSales:            number;
  totalInvoices:         number;
  paidAmount:            number;
  unpaidAmount:          number;
  paidInvoices:          number;
  unpaidInvoices:        number;
  partiallyPaidInvoices: number;
  averageInvoiceValue:   number;
  periodStart:           string;
  periodEnd:             string;
}

export interface DayEndSummaryDTO {
  businessDate:       string;
  salesSummary:       SalesSummaryDTO;
  pendingInvoices:    SalesInvoiceListDTO[];
  hasPendingInvoices: boolean;
}

const BASE = '/company-settings';

export const companySettingsService = {
  getCompanySettings: () =>
    apiService.get<CompanySettings>(BASE),

  /**
   * Lightweight endpoint — any authenticated user, no Settings permission needed.
   * Returns logoUrl + currencyCode so every page can format currency correctly.
   */
  getLogoUrl: () =>
    apiService.get<{ logoUrl: string | null; currencyCode: string; defaultPrintOutput: PrintOutputType | null; defaultPrinterName: string | null }>(`${BASE}/logo-url`),

  updateCompanySettings: (data: UpdateCompanySettingsRequest) =>
    apiService.put<CompanySettings>(BASE, data),

  getAvailablePrinters: () =>
    apiService.get<{ printers: string[] }>(`${BASE}/printers`),

  getBusinessDate: () =>
    apiService.get<{ businessDate: string }>(`${BASE}/business-date`),

  advanceBusinessDay: () =>
    apiService.post<{ businessDate: string }>(`${BASE}/day-end`, {}),

  getDayEndSummary: () =>
    apiService.get<DayEndSummaryDTO>(`${BASE}/day-end-summary`),

  /**
   * Upload a new company logo.
   *
   * WHY the explicit header override:
   *   The shared `apiClient` instance has `Content-Type: application/json` in its
   *   defaults.  Axios 1.x does NOT auto-remove instance-level defaults even when
   *   the body is FormData, so the request would otherwise arrive at the server
   *   with the wrong content-type, causing IFormFile binding to return null.
   *
   *   Setting `'Content-Type': null` tells AxiosHeaders to DELETE that header
   *   for this call.  The browser then sets the correct
   *   `multipart/form-data; boundary=...` header automatically.
   */
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return apiClient.post<ApiResponse<{ logoUrl: string }>>(
      `${BASE}/logo`,
      form,
      { headers: { 'Content-Type': null } },   // null → Axios deletes the header
    );
  },
};
